// @ts-nocheck
import { useDrag } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useEffect, useRef } from 'react';
import { StateShape as ReduxStateShape } from '../reducers';
import { searchGreySvg } from '../assets/images';
import { actionCreators as componentCreators } from '../reducers/components';
import { actionCreators as publicComponentCreators } from '../reducers/publicComponents';
import Thumbnail from '../components/BaseComponents/Thumbnail';

interface WidgetProps {
    id: string;
    label?: string;
    icon?: string;
    isRefComponent?: boolean;
    isPublicRefComponent?: boolean;
    childern?: JSX.Element | JSX.Element[];
}

export const LazyThumbnail = ({ resourceId, size, isPublicRefComponent }) => {
    const [isInView, setIsInView] = useState(false);
    const ref: any = useRef();

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setIsInView(true);
                    observer.disconnect();
                }
            },
            {
                threshold: 0.1,
            }
        );

        if (ref?.current) {
            const currentRef = ref.current;
            observer.observe(currentRef);

            return () => {
                observer.unobserve(currentRef);
            };
        }
    }, [ref]);

    return (
        <div ref={ref}>
            {isInView && (
                <Thumbnail
                    resourceId={resourceId}
                    size={size}
                    isPublicRefComponent={isPublicRefComponent}
                />
            )}
        </div>
    );
};

export const Component: React.FC<WidgetProps> = ({
    id,
    label,
    icon,
    isRefComponent,
    isPublicRefComponent,
}): JSX.Element => {
    const [{ isDragging }, drag] = useDrag(() => ({
        type: 'WIDGET',
        item: () => {
            return { id, isRefComponent, isPublicRefComponent, dragMode: 'create' };
        },
        collect: (monitor: any) => ({
            isDragging: monitor.isDragging(),
        }),
    }));

    const getReosurceId = (id: string) => {
        if (id?.includes('public/')) {
            return `/screenshots/components/${id}`;
        } else {
            return `/assets/${id}`;
        }
    };

    return (
        <div
            ref={drag}
            className={'component-item'}
            style={{ opacity: isDragging ? 0.5 : 1, cursor: 'move' }}
        >
            <LazyThumbnail
                resourceId={getReosurceId(id)}
                size={'1920x1080'}
                isPublicRefComponent={isPublicRefComponent}
            />
            <div className="component-button">{label || 'Untitled'}</div>
        </div>
    );
};

interface ComponentGalleryProps {
    childern?: JSX.Element | JSX.Element[];
}

const ComponentGallery: React.FC<ComponentGalleryProps> = (): React.ReactElement => {
    const [search, setSearch] = useState('');
    const [activeTab, setActiveTab] = useState('referenceComponents');

    const dispatch = useDispatch();

    const { components } = useSelector((state: ReduxStateShape) => state.components);
    const { components: publicComponents } = useSelector(
        (state: ReduxStateShape) => state.publicComponents
    );
    const [filteredComponents, setFilteredComponents] = useState<any>(components);
    const [filteredPublicComps, setFilteredPublicComps] = useState<any>(publicComponents);

    useEffect(() => {
        if (components === null) {
            // to pull max of 1000 reference components
            dispatch(componentCreators.listComponents('public', '', false, {}, {}, {}, 1000, 0));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [components]);

    useEffect(() => {
        if (publicComponents === null) {
            // to pull max of 1000 reference components
            dispatch(
                publicComponentCreators.listComponents('public', '', false, {}, {}, {}, 1000, 0)
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [publicComponents]);

    const componentsList: any = {
        referenceComponents: {
            group: [],
            id: 'referenceComponents',
            title: 'Reference\nComponents',
        },
        publicComponents: { group: [], id: 'publicComponents', title: 'Public\nComponents' },
    };

    // Handles search functioanlity for components
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        if (activeTab === 'referenceComponents') {
            setFilteredComponents(
                components.filter((component: any) =>
                    component.name.toLowerCase().includes(e.target.value.toLowerCase())
                )
            );
        } else {
            setFilteredPublicComps(
                publicComponents.filter((component: any) =>
                    component.name.toLowerCase().includes(e.target.value.toLowerCase())
                )
            );
        }
    };

    const handleTabChange = (tab: string) => {
        // Reset serach on tab change
        setSearch('');
        setFilteredComponents(components);
        setFilteredPublicComps(publicComponents);
        setActiveTab(tab);
    };

    return (
        <div className="components-gallery">
            <h3 className="sidebar-header">Components</h3>
            <>
                <div className="components-container">
                    <div className="input-search-widget">
                        <img className="search-icon" src={searchGreySvg} alt="Search icon" />
                        <input
                            type="text"
                            placeholder="Search Components"
                            value={search}
                            onChange={handleChange}
                            className={'input-textfield'}
                        />
                    </div>
                    {filteredComponents &&
                        filteredComponents?.length > 0 &&
                        filteredComponents?.forEach((v: any) => {
                            if (v.name) {
                                componentsList.referenceComponents.group.push(
                                    <React.Fragment key={v.id}>
                                        <Component
                                            id={v.id}
                                            label={v.name}
                                            icon={v.icon}
                                            isRefComponent={true}
                                        />
                                    </React.Fragment>
                                );
                            }
                        })}
                    {filteredPublicComps &&
                        filteredPublicComps?.length > 0 &&
                        filteredPublicComps?.forEach((v: any) => {
                            if (v?.name) {
                                componentsList?.publicComponents?.group?.push(
                                    <React.Fragment key={v.id}>
                                        <Component
                                            id={v.id}
                                            label={v.name}
                                            icon={v.icon}
                                            isRefComponent={false}
                                            isPublicRefComponent={true}
                                        />
                                    </React.Fragment>
                                );
                            }
                        })}

                    <ul className="components-tabs accordian-tabs" role="tablist">
                        {Object.keys(componentsList).map((key: string, index: number) => (
                            <li
                                className={`nav-item ${
                                    activeTab === componentsList?.[key]?.id && 'active'
                                }`}
                                key={componentsList?.[key]?.id}
                                onClick={() => handleTabChange(componentsList?.[key]?.id)}
                            >
                                <pre className="tab-title">{componentsList?.[key]?.title}</pre>
                            </li>
                        ))}
                    </ul>
                    {Object.keys(componentsList).map((key: string, index: number) => (
                        <div
                            key={index}
                            className="components-list"
                            style={{ display: key === activeTab ? 'block' : 'none' }}
                        >
                            {componentsList?.[key]?.group?.length > 0 ? (
                                <>{componentsList?.[key]?.group}</>
                            ) : (
                                <span className="no-component-text">No Component Found.</span>
                            )}
                        </div>
                    ))}
                </div>
            </>
        </div>
    );
};

export default ComponentGallery;
